import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Modal, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import {
  getServiceCategories,
  addServiceCategory,
  updateServiceCategory,
  deleteServiceCategory
} from '../../services/firebase/categoryService';
import { getServices } from '../../services/firebase/serviceService';

const AdminServiceCategories = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [validated, setValidated] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cats, svcs] = await Promise.all([getServiceCategories(), getServices()]);
      setCategories(cats);
      setServices(svcs);
    } catch (err) {
      setError('Failed to load service categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showMessage = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleShowModal = (category = null) => {
    if (category) {
      setCurrent({ ...category, serviceIds: category.serviceIds || [] });
    } else {
      setCurrent({ name: '', description: '', serviceIds: [], active: true, highlight: false });
    }
    setValidated(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrent(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrent((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleService = (serviceId) => {
    setCurrent((prev) => {
      const ids = prev.serviceIds || [];
      return ids.includes(serviceId)
        ? { ...prev, serviceIds: ids.filter((id) => id !== serviceId) }
        : { ...prev, serviceIds: [...ids, serviceId] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || !current.name.trim()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    try {
      if (current.id) {
        await updateServiceCategory(current.id, current);
        showMessage('Category updated successfully.');
      } else {
        await addServiceCategory(current);
        showMessage('Category created successfully.');
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      setError('Failed to save the category. Please try again.');
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) return;

    // Move the item within the array, then assign a clean 0..N order to every
    // category. This is robust even when stored orders are duplicated or gapped.
    const reordered = [...categories];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    const renumbered = reordered.map((cat, i) => ({ ...cat, order: i }));

    // Optimistic UI update so the move feels instant.
    setCategories(renumbered);

    try {
      await Promise.all(
        renumbered.map((cat) => updateServiceCategory(cat.id, cat))
      );
    } catch (err) {
      setError('Failed to reorder categories.');
      fetchData(); // revert to server state on failure
    }
  };

  const confirmDelete = (category) => {
    setToDelete(category);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteServiceCategory(toDelete.id);
      showMessage('Category deleted successfully.');
      setShowDeleteModal(false);
      setToDelete(null);
      fetchData();
    } catch (err) {
      setError('Failed to delete the category.');
    }
  };

  const serviceName = (id) => {
    const svc = services.find((s) => s.id === id);
    return svc ? svc.title : null;
  };

  return (
    <Container className="py-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-1">Service Categories</h2>
          <p className="text-muted mb-0">
            Group services into categories. On the homepage, visitors filter services by these categories.
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Category
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : categories.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No service categories yet. Click “Add Category” to create your first one.
            </Alert>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Order</th>
                  <th>Name</th>
                  <th>Services</th>
                  <th>Status</th>
                  <th style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, index) => (
                  <tr key={cat.id}>
                    <td>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        disabled={index === 0}
                        onClick={() => handleMove(index, -1)}
                        title="Move up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={index === categories.length - 1}
                        onClick={() => handleMove(index, 1)}
                        title="Move down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </Button>
                    </td>
                    <td>
                      <strong>{cat.name}</strong>
                      {cat.highlight && (
                        <Badge bg="warning" text="dark" className="ms-2">★ Best Selling</Badge>
                      )}
                      {cat.description && (
                        <>
                          <br />
                          <small className="text-muted">{cat.description}</small>
                        </>
                      )}
                    </td>
                    <td>
                      {(cat.serviceIds || []).length === 0 ? (
                        <span className="text-muted">No services</span>
                      ) : (
                        <div className="d-flex flex-wrap gap-1">
                          {(cat.serviceIds || []).map((id) => {
                            const name = serviceName(id);
                            return name ? (
                              <Badge bg="light" text="dark" key={id} className="border">
                                {name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </td>
                    <td>
                      {cat.active !== false ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Hidden</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleShowModal(cat)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => confirmDelete(cat)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{current?.id ? 'Edit Category' : 'Add Category'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="categoryName">
              <Form.Label>Category Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={current?.name || ''}
                onChange={handleInputChange}
                placeholder="e.g., Aesthetics, Weight Loss, Pain Relief"
                required
              />
              <Form.Control.Feedback type="invalid">Please provide a category name.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="categoryDescription">
              <Form.Label>Description <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={current?.description || ''}
                onChange={handleInputChange}
                placeholder="Short description shown to help you organize categories."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Services in this category</Form.Label>
              {services.length === 0 ? (
                <Alert variant="warning" className="mb-0">
                  No services available yet. Add services under “Services” first.
                </Alert>
              ) : (
                <div
                  className="border rounded p-3"
                  style={{ maxHeight: '260px', overflowY: 'auto' }}
                >
                  <Row>
                    {services.map((svc) => (
                      <Col md={6} key={svc.id}>
                        <Form.Check
                          type="checkbox"
                          id={`svc-${svc.id}`}
                          label={svc.title}
                          checked={(current?.serviceIds || []).includes(svc.id)}
                          onChange={() => toggleService(svc.id)}
                          className="mb-2"
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
              <Form.Text className="text-muted">
                {(current?.serviceIds || []).length} service(s) selected. A service can be in multiple categories.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2" controlId="categoryActive">
              <Form.Check
                type="checkbox"
                name="active"
                label="Active (show this category on the homepage)"
                checked={current?.active !== false}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-1" controlId="categoryHighlight">
              <Form.Check
                type="checkbox"
                name="highlight"
                label="Highlight as “Best Selling” (pinned first, gold tab)"
                checked={!!current?.highlight}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Use this for your featured category. It appears first on the homepage with a distinct color.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the category “{toDelete?.name}”? The services themselves will not be
          deleted — they’ll just be uncategorized.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminServiceCategories;
